import { palette } from '@/brand-utgl/theme';
import { AlertType, UniAlert } from '@/design-system/components/alerts';
import { UniButton } from '@/design-system/components/buttons';
import { ButtonVariant } from '@/design-system/components/buttons/useButton.hook';
import { Divider } from '@/design-system/components/Divider';
import { getObjectScreenNameFromPath, popStateLockedMap } from '@/design-system/components/navigatorEmbed/UtxAppNavigatorEmbed';
import { UniTabHeader } from '@/design-system/components/tabs';
import { H6, TextBody, TextCaption, TextSmall } from '@/design-system/components/typography';
import { iframePostMessage } from '@/utils/iframePostMessage';
import { useFocusEffect, useNavigation } from '@react-navigation/native';
import { ScrollView, SxProp, View } from 'dripsy';
import _ from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { RootStackRoutes } from 'types';
import BillPaymentApp from '../..';
import { UseBillPaymentFormProp } from '../../hooks/useBillPaymentForm.hook';
import { QuoteResponse, UseCreateBillPaymentProp } from '../../hooks/useCreateBillPayment.hook';
import { BillPaymentStackRoutes } from '../../payment.navigator';
import { mapUrlLink, onGoBack } from '../../utils/utils';
import { HeaderBackgroundImageComponent } from '../components/HeaderBackgroundImageComponent';
import { DetailFormMobileComponent } from './DetailFormMobile.component';
export interface BillPaymentFormNativeProps {
  useBillPaymentForm: UseBillPaymentFormProp;
  useCreateBillPayment: UseCreateBillPaymentProp;
  imageComponent: JSX.Element;
}

export const BillPaymentFormMobileComponent: React.FC<BillPaymentFormNativeProps> = ({
  useBillPaymentForm,
  useCreateBillPayment,
  imageComponent
}) => {
  const { t } = useTranslation('global-bill-payment');
  const [submittedButton, setSubmittedButton] = useState(false);
  const navigation = useNavigation();
  const insets = useSafeAreaInsets();
  const billPaymentForm = useBillPaymentForm;
  const {
    detailForm: {
      bank,
      bankAccountNumber,
      debitAccountWallerId,
      additionalDetail,
      assetName,
      amount,
      walletId,
      debitAccountWalletName,
      onValidateVerification
    },
    scheduleForm: { remarks, onValidateScheduleForm },
    general: {
      validated,
      debitAmount,
      billPaymentFee,
      isLoadingFee,
      setValidated,
      setFlagSubmitted,
      quoteDataRequest,
      documentSelected,
      quoteResponseProps,
      quoteDataRequestProps
    }
  } = billPaymentForm;
  const {
    general: { alertMessage, setAlertMessage, mapSearchWithResponse },
    instruction: { postQuote, isLoadingQuote, mapQuoteDataRequest }
  } = useCreateBillPayment;

  useEffect(() => {
    if (submittedButton && validated) {
      setSubmittedButton(false);
      setValidated(false);
      setAlertMessage('');

      postQuote(
        quoteDataRequest,
        (data) => {
          const response: QuoteResponse = data.data;

          navigation.navigate(RootStackRoutes.BillPayment, {
            screen: BillPaymentStackRoutes.FormSchedule,
            params: {
              request: mapQuoteDataRequest(quoteDataRequest, response),
              response: mapSearchWithResponse(response, quoteResponseProps!),
              detailForm: {
                bank: bank ?? '',
                debitAccountWallerId: debitAccountWallerId ?? '',
                additionalDetail: additionalDetail ?? '',
                assetName: assetName ?? '',
                amount: amount ?? '',
                walletId: walletId ?? '',
                bankAccountNumber: bankAccountNumber ?? '',
                debitAccountWalletName: debitAccountWalletName ?? '',
                remarks: remarks ?? ''
              },
              documentSelected: documentSelected,
              paymentType: useCreateBillPayment.general.paymentType!
            }
          });
        },
        (error) => {
          console.log(error);
          const { data } = error.response || ({} as any);
          const errorMessage = data && _.isString(data.message) ? data.message : error.message;
          if (!onValidateVerification(errorMessage)) setAlertMessage(errorMessage);
        }
      );
    }
    const timeout = setTimeout(() => setSubmittedButton(false), 500);
    return () => clearTimeout(timeout);
  }, [submittedButton, validated]);

  const onBack = () => {
    if (Platform.OS === 'web') {
      popStateLockedMap.set('popstate', true);
      onGoBack(navigation);
      setTimeout(() => popStateLockedMap.set('popstate', false), 500);
    } else {
      onGoBack(navigation);
    }
  };

  const onPopState = (event: any) => {
    window.history.pushState(event.state, '');
    setTimeout(() => {
      const popStateLocked = popStateLockedMap.get('popstate') ?? false;
      if (!document.location.href || popStateLocked) return;
      const { parentName, subName } = mapUrlLink(document.location.href);
      var screenNameObject = getObjectScreenNameFromPath([BillPaymentApp], `/${subName}`);
      if (
        screenNameObject.screenName &&
        screenNameObject.routeStackName === parentName &&
        screenNameObject.screenName === BillPaymentStackRoutes.Form
      ) {
        onBack();
      }
    }, 200);
  };

  useFocusEffect(
    useCallback(() => {
      if (Platform.OS === 'web') {
        window.addEventListener('popstate', onPopState);
        return () => {
          window.removeEventListener('popstate', onPopState);
        };
      }
    }, [])
  );

  return (
    <View sx={sxStyles.container}>
      <View sx={{ ...sxStyles.header, ...{ pt: insets.top } }}>
        <HeaderBackgroundImageComponent />
        <UniTabHeader
          title={t('global-bill-payment::glossary.title--payment-instruction')}
          onBack={() => onBack()}
          labelColor="$white"
          sx={sxStyles.tab}
        />
      </View>
      <ScrollView sx={sxStyles.scrollView} contentContainerStyle={{ flexGrow: 1 }} showsVerticalScrollIndicator={false}>
        <View sx={sxStyles.body}>
          <View sx={sxStyles.content}>
            {imageComponent}
            <DetailFormMobileComponent sx={sxStyles.detailFormNative} billPaymentProp={billPaymentForm} />
          </View>

          <Divider sx={{ ...sxStyles.viewDivider, ...{ mt: '$15' } }} />

          <View sx={sxStyles.amountLayout}>
            <TextCaption semiBold={true}>{t('global-bill-payment::glossary.debit-amount')}</TextCaption>
            <H6 sx={{ color: palette.Red.$600, mt: '$1' }} bold={true}>
              {debitAmount}
            </H6>
            <TextBody sx={sxStyles.marginTop4}>
              {t('global-bill-payment::payment-details.glossary.text--instruction-fee-included').replace('{{amount}}', billPaymentFee)}
            </TextBody>
          </View>

          <Divider sx={sxStyles.viewDivider} />

          <View sx={sxStyles.viewNotice}>
            <TextSmall semiBold={true} sx={sxStyles.textNotice}>
              {t('global-bill-payment::instruction.title--notice')}
            </TextSmall>
            <View sx={{ ...sxStyles.viewRow, ...sxStyles.marginTop4 }}>
              <TextSmall sx={{ ...sxStyles.textNotice, ...{ width: 20, textAlign: 'center' } }}>{`\u2022`}</TextSmall>
              <View sx={{ ...sxStyles.viewColumn, ...{ flex: 1 } }}>
                <TextSmall sx={sxStyles.textNotice}>{t('global-bill-payment::instruction.text--notice-as-the-bill-payment')}</TextSmall>
              </View>
            </View>
            <View sx={{ ...sxStyles.viewRow, ...sxStyles.marginTop4 }}>
              <TextSmall sx={{ ...sxStyles.textNotice, ...{ width: 20, textAlign: 'center' } }}>{`\u2022`}</TextSmall>
              <View sx={{ ...sxStyles.viewColumn, ...{ flex: 1 } }}>
                <TextSmall sx={sxStyles.textNotice}>{t('global-bill-payment::instruction.text--notice-make-sure')}</TextSmall>
                <View sx={sxStyles.bottomButton}>
                  <UniButton
                    variant={ButtonVariant.outline1}
                    sx={sxStyles.viewButton}
                    label={t('global-bill-payment::instruction.button--injection')}
                    size="small"
                    onPress={() => iframePostMessage('showInjectionInstruction', true)}
                  />
                  <View sx={{ mr: '$2' }} />
                  <UniButton
                    variant={ButtonVariant.outline1}
                    sx={sxStyles.viewButton}
                    label={t('global-bill-payment::instruction.button--exchange')}
                    onPress={() => iframePostMessage('moveToExchange', true)}
                    size="small"
                  />
                </View>
              </View>
            </View>
          </View>

          <View sx={sxStyles.buttonLayout}>
            <View sx={sxStyles.buttonContinue}>
              {!_.isEmpty(alertMessage) && <UniAlert type={AlertType.Error} message={alertMessage} sx={sxStyles.viewAlert} />}
              <UniButton
                label={t('global-bill-payment::glossary.continue')}
                onPress={() => {
                  setFlagSubmitted(true);
                  setSubmittedButton(true);
                  onValidateScheduleForm();
                }}
                sx={sxStyles.button}
                size="medium"
                loading={isLoadingQuote}
                disabled={isLoadingFee || isLoadingQuote}
              />
            </View>
          </View>
        </View>
      </ScrollView>
    </View>
  );
};

const sxStyles: Record<string, SxProp> = {
  container: {
    backgroundColor: '$backgroundPrimary',
    width: '100%',
    height: '100%'
  },
  body: {
    flex: 1
  },
  scrollView: {
    padding: '$3'
  },
  header: {
    flexDirection: 'column'
  },
  tab: {
    backgroundColor: 'transparent',
    width: '98%'
  },
  content: {
    flexDirection: 'column',
    zIndex: 2
  },
  buttonLayout: {
    flexDirection: 'column'
  },
  button: {
    width: '100%',
    height: '$11'
  },
  viewDivider: {
    my: '$5'
  },
  detailFormNative: {
    zIndex: 3,
    mt: '$5'
  },
  marginTop4: {
    mt: '$1'
  },
  buttonContinue: {
    flex: 1,
    justifyContent: 'flex-end'
  },
  viewNotice: {
    flexDirection: 'column',
    mb: '$13'
  },
  flex8: {
    flex: 8
  },
  flex92: {
    flex: 92
  },
  flex208: {
    flex: 208
  },
  viewColumn: {
    flexDirection: 'column'
  },
  viewButton: {
    px: '$5'
  },
  textNotice: {
    color: '#666666'
  },
  bottomButton: {
    flexDirection: 'row',
    mt: '$3',
    flex: 1
  },
  viewRow: {
    flexDirection: 'row'
  },
  viewAlert: {
    mb: '$3'
  },
  amountLayout: {
    flexDirection: 'column'
  }
};
